/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { useFormikContext } from 'formik'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  PLUGIN_VIEW_ID_LABEL,
  PLUGIN_VIEW_OPTIONS,
  PLUGIN_VIEW_TABS,
  withPluginView,
  type PluginEmbedSettings,
  type PluginView,
} from '@sportninja/common/features/account/apiAccess'
import { SegmentedInput } from 'src/components/ts-components/SegmentedInput'
import {
  SelectField,
  SwitchField,
  TextField,
} from '../../../components/FormikField'

/** The initial tab select's value, and placeholder, for the view's own first tab. */
const DEFAULT_TAB = ''
const DEFAULT_TAB_LABEL = 'Default'

/** The view, the id of what it shows, the tab it opens on and URL sync. */
export const EmbedGroup = () => {
  const { values, initialValues, setValues } =
    useFormikContext<PluginEmbedSettings>()
  const idLabel = capitalizeWords(PLUGIN_VIEW_ID_LABEL[values.view])

  const changeView = (view: PluginView) => {
    const next = withPluginView(values, view)
    // Organization starts from the account's own organization again.
    setValues(
      view === 'organization'
        ? { ...next, entityId: initialValues.entityId }
        : next
    )
  }

  return (
    <div css={groupStyle}>
      <SegmentedInput
        label={capitalizeWords('View')}
        options={PLUGIN_VIEW_OPTIONS.map(({ label, value }) => ({
          label: capitalizeWords(label),
          value,
        }))}
        value={values.view}
        onChange={changeView}
        hint='The entry point of your plugin: the page it opens on'
      />
      <TextField name='entityId' label={idLabel} placeholder={idLabel} />
      <SelectField
        name='initialTab'
        label={capitalizeWords('Initial Tab')}
        placeholder={DEFAULT_TAB_LABEL}
        options={[
          { label: DEFAULT_TAB_LABEL, value: DEFAULT_TAB },
          ...PLUGIN_VIEW_TABS[values.view].map(({ id, label }) => ({
            label: capitalizeWords(label),
            value: id,
          })),
        ]}
      />
      <SwitchField
        name='syncUrl'
        label={capitalizeWords('Sync URL With Host Page')}
        hint='Deep links in the host address bar'
      />
    </div>
  )
}

const groupStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`
